import React, { useState, lazy } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, BookOpen } from 'lucide-react';
import { cn } from '../utils/cn';
export interface BannerItem {
  id: string;
  label: string;
  title: string;
  meta?: string;
  badge?: string;
  description: string;
  image: string;
}
interface BannerSliderProps {
  items: BannerItem[];
  /** 'volume' uses teal accent, 'bonus' uses amber accent */
  accent?: 'volume' | 'bonus';
}
export function BannerSlider({
  items,
  accent = 'volume'
}: BannerSliderProps) {
  const [openId, setOpenId] = useState<string | null>(items[0]?.id ?? null);
  return <div className="flex flex-col gap-4 sm:gap-5" data-id="element-11">
      {items.map((item, index) => {
      const isOpen = openId === item.id;
      return <motion.div key={item.id} initial={{
        opacity: 0,
        y: 24
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-60px'
      }} transition={{
        duration: 0.5,
        delay: index * 0.08
      }} data-id="element-12">
            <button type="button" onClick={() => setOpenId(isOpen ? null : item.id)} aria-expanded={isOpen} className={cn('group relative block w-full overflow-hidden rounded-xl sm:rounded-2xl text-left ring-1 transition-all duration-300', 'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900', isOpen ? accent === 'bonus' ? 'ring-amber-400/50 shadow-2xl shadow-amber-500/10' : 'ring-brand-400/50 shadow-2xl shadow-brand-500/10' : 'ring-white/10 hover:ring-white/25 shadow-lg hover:shadow-2xl')} data-id="element-13">
              {/* 3:1 banner */}
              <div className="relative aspect-[3/1] w-full overflow-hidden" data-id="element-14">
                <img src={item.image} alt={item.title} loading="lazy" className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" data-id="element-15" />
                {/* Dark cinematic overlay, stronger on the left for legibility */}
                <div className="absolute inset-0 bg-gradient-to-r from-slate-950/90 via-slate-950/55 to-slate-950/20" data-id="element-16" />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950/70 via-transparent to-transparent" data-id="element-17" />

                {/* Embedded title + number */}
                <div className="absolute inset-0 flex items-center" data-id="element-18">
                  <div className="flex items-center gap-4 sm:gap-6 pl-4 pr-4 sm:pl-8" data-id="element-19">
                    <span className={cn('flex-none font-display font-extrabold leading-none tracking-tight tabular-nums', 'text-4xl sm:text-6xl lg:text-7xl', accent === 'bonus' ? 'text-amber-400' : 'text-brand-400')} data-id="element-20">
                      {item.id}
                    </span>
                    <div className="min-w-0" data-id="element-21">
                      <span className={cn('block text-[10px] sm:text-xs font-semibold uppercase tracking-[0.18em]', accent === 'bonus' ? 'text-amber-300/90' : 'text-brand-300/90')} data-id="element-22">
                        {item.label}
                      </span>
                      <h3 className="mt-0.5 sm:mt-1 font-display text-lg font-extrabold uppercase leading-tight tracking-tight text-white drop-shadow sm:text-3xl lg:text-4xl" data-id="element-23">
                        {item.title}
                      </h3>
                      {item.meta && <span className="mt-1 hidden items-center text-xs font-medium text-slate-300 sm:inline-flex" data-id="element-24">
                          <BookOpen className="mr-1.5 h-3.5 w-3.5" data-id="element-25" />
                          {item.meta}
                        </span>}
                    </div>
                  </div>
                </div>

                {/* Badge */}
                {item.badge && <span className={cn('absolute right-3 top-3 sm:right-5 sm:top-5 inline-flex items-center rounded-full px-2.5 py-0.5 text-[10px] sm:text-xs font-semibold uppercase tracking-wider ring-1 ring-inset backdrop-blur-sm', accent === 'bonus' ? 'bg-amber-400/20 text-amber-300 ring-amber-400/40' : 'bg-brand-400/20 text-brand-200 ring-brand-400/40')} data-id="element-26">
                    {item.badge}
                  </span>}

                {/* Open/expand indicator */}
                <span className={cn('absolute bottom-3 right-3 sm:bottom-5 sm:right-5 flex h-9 w-9 sm:h-11 sm:w-11 items-center justify-center rounded-full ring-1 backdrop-blur-md transition-all duration-300', accent === 'bonus' ? 'bg-amber-400/20 ring-amber-400/40 text-amber-200' : 'bg-brand-400/20 ring-brand-400/40 text-brand-100', isOpen && 'rotate-45')} data-id="element-27">
                  <Plus className="h-4 w-4 sm:h-5 sm:w-5" data-id="element-28" />
                </span>
              </div>

              {/* Expandable description */}
              <AnimatePresence initial={false} data-id="element-29">
                {isOpen && <motion.div initial={{
              height: 0,
              opacity: 0
            }} animate={{
              height: 'auto',
              opacity: 1
            }} exit={{
              height: 0,
              opacity: 0
            }} transition={{
              duration: 0.3,
              ease: 'easeInOut'
            }} className="bg-slate-900/95" data-id="element-30">
                    <div className="px-4 py-4 sm:px-8 sm:py-6" data-id="element-31">
                      {item.meta && <span className="mb-2 inline-flex items-center text-xs font-medium text-slate-400 sm:hidden" data-id="element-32">
                          <BookOpen className="mr-1.5 h-3.5 w-3.5" data-id="element-33" />
                          {item.meta}
                        </span>}
                      <p className="text-sm leading-relaxed text-slate-300 sm:text-base" data-id="element-34">
                        {item.description}
                      </p>
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </button>
          </motion.div>;
    })}
    </div>;
}